import React, { FunctionComponent, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";
import { Radio } from "@/components/Radio/Radio";

import { SENIORITY_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/seniority.constants";
import { DEPARTMENT_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/department.constants";

import { MultiAutoComplete } from "@/components/MultiAutoComplete/MultiAutoComplete";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { Information } from "@/components/Information/Information";

import { SelectField } from "@/components/SelectField/SelectField";

import { useAppSelector } from "@/redux/hooks";
import { scoreSelector } from "@/pages/Private/redux/score/score.slice";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useGetScoreQuery } from "@/pages/Private/redux/score/score.api";

import { EnrichmentFilterConfig } from "../../ListEditDetails";
import { IcpPosition } from "../../enum/icp.enum";
import { List } from "../../../../schema/list";

import { CustomTitleOption } from "./CustomTitleOption";
import { SearchEngineBadge } from "./SearchEngineBadge";
import { SEARCH_ENGINE_BY_POSITION } from "./searchProfileEngine";

interface PositionColumnProps {
	list: List;
	titleFilters?: EnrichmentFilterConfig;
	setTitleFilters: React.Dispatch<React.SetStateAction<EnrichmentFilterConfig | undefined>>;
	prio: "first" | "second" | "third";
	error?: boolean;
}

/** Left column of a search profile: which position/job title to search for (four engine options). */
export const PositionColumn: FunctionComponent<PositionColumnProps> = ({
	list,
	titleFilters,
	setTitleFilters,
	prio,
	error,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`defineIcps.defineIcp.${key}`);

	// Score & persona category data from Redux
	useGetScoreQuery();
	const { score } = useAppSelector(scoreSelector);
	const { scoreCompanyCategories } = useAppSelector(listSelector);

	const filteredPersonaCategories = useMemo(() => {
		const allPersonaCategories = score?.prospectPersonaCategories ?? [];

		if (scoreCompanyCategories.length === 0) {
			return allPersonaCategories;
		}

		const selectedCompanyCategoryIds = new Set(
			scoreCompanyCategories.map((c) => c.id).filter((id): id is number => id !== undefined)
		);

		return allPersonaCategories.filter((persona) =>
			persona.scoreCompanyCategories.some(
				(cc) => cc.id !== undefined && selectedCompanyCategoryIds.has(cc.id)
			)
		);
	}, [score?.prospectPersonaCategories, scoreCompanyCategories]);

	const selectedPersonaCategory = useMemo(() => {
		const categoryId = titleFilters?.[prio]?.personaCategoryId;

		if (!categoryId) {
			return null;
		}

		return filteredPersonaCategories.find((c) => c.id === categoryId) ?? null;
	}, [filteredPersonaCategories, titleFilters, prio]);

	return (
		<div className="w-full flex flex-col self-stretch">
			<div className="text-sm text-text mb-3">{ts("positionOrJobTitle")}</div>
			<div className="mb-4">
				<Radio
					isChecked={titleFilters?.[prio]?.position === IcpPosition.CEO_AND_SIMILAR}
					name="position"
					value={IcpPosition.CEO_AND_SIMILAR}
					onChange={() =>
						setTitleFilters({
							...titleFilters,
							[prio]: {
								...titleFilters?.[prio],
								position: IcpPosition.CEO_AND_SIMILAR,
							},
						})
					}
				>
					<span className="text-text-meta ml-2 flex items-center gap-1.5">
						{ts("ceoAndSimilar")}
						<Information information={ts("ceoAndSimilarInfo")} />
						<SearchEngineBadge meta={SEARCH_ENGINE_BY_POSITION[IcpPosition.CEO_AND_SIMILAR]} />
					</span>
				</Radio>
			</div>
			<div className="mb-4">
				<Radio
					isChecked={titleFilters?.[prio]?.position === IcpPosition.PERSONA_SEGMENT}
					name="position"
					value={IcpPosition.PERSONA_SEGMENT}
					onChange={() =>
						setTitleFilters({
							...titleFilters,
							[prio]: {
								...titleFilters?.[prio],
								position: IcpPosition.PERSONA_SEGMENT,
								personaCategoryId: undefined,
								title: undefined,
								similarTitles: [],
								similarTitleSearch: false,
							},
						})
					}
				>
					<span className="text-text-meta ml-2 flex items-center gap-1.5">
						{ts("usePersonaSegment")}
						<SearchEngineBadge meta={SEARCH_ENGINE_BY_POSITION[IcpPosition.PERSONA_SEGMENT]} />
					</span>
				</Radio>
				{titleFilters?.[prio]?.position === IcpPosition.PERSONA_SEGMENT && (
					<div className="mt-2 ml-8 flex flex-col items-start">
						<div className="mb-4 w-full max-w-xs">
							<SelectField
								options={filteredPersonaCategories.map((c) => ({
									title: c.category,
									value: c.id ?? null,
								}))}
								selectedOption={titleFilters?.[prio]?.personaCategoryId ?? null}
								handleSelect={(value) => {
									setTitleFilters({
										...titleFilters,
										[prio]: {
											...titleFilters?.[prio],
											personaCategoryId: value as number,
										},
									});
								}}
								placeholder={ts("selectPersonaCategory")}
							/>
						</div>
						<div className="flex flex-row items-center mb-2">
							<Checkbox
								isChecked={titleFilters?.[prio]?.decisionMakersOnly || false}
								isDisabled={!titleFilters?.[prio]?.personaCategoryId}
								onChange={() => {
									setTitleFilters({
										...titleFilters,
										[prio]: {
											...titleFilters?.[prio],
											decisionMakersOnly: !titleFilters?.[prio]?.decisionMakersOnly,
										},
									});
								}}
							>
								{ts("onlySearchForDecisionMakersHeadOfAndHigher")}
							</Checkbox>
						</div>
						{selectedPersonaCategory && selectedPersonaCategory.jobsToInclude.length > 0 && (
							<div className="mt-2">
								<div className="text-ssm text-text-meta mb-1">{ts("exampleJobTitles")}</div>
								<div className="flex flex-wrap gap-1">
									{selectedPersonaCategory.jobsToInclude.map((jobTitle) => {
										const isExcluded =
											titleFilters?.[prio]?.excludedPersonaJobTitles?.includes(jobTitle);

										return (
											<span
												key={jobTitle}
												className={classNames(
													"cursor-pointer inline-flex text-xs px-2 py-1 rounded-md mr-2 mb-1",
													isExcluded ? "bg-white text-text-meta" : "bg-blue-action text-white"
												)}
												onClick={() => {
													const currentExcluded =
														titleFilters?.[prio]?.excludedPersonaJobTitles || [];
													const newExcluded = isExcluded
														? currentExcluded.filter((t) => t !== jobTitle)
														: [...currentExcluded, jobTitle];

													setTitleFilters({
														...titleFilters,
														[prio]: {
															...titleFilters?.[prio],
															excludedPersonaJobTitles: newExcluded,
														},
													});
												}}
											>
												{jobTitle}
											</span>
										);
									})}
								</div>
							</div>
						)}
					</div>
				)}
			</div>
			<CustomTitleOption
				error={error}
				list={list}
				prio={prio}
				setTitleFilters={setTitleFilters}
				titleFilters={titleFilters}
			/>
			<div className="mb-4">
				<Radio
					isChecked={titleFilters?.[prio]?.position === IcpPosition.DEPARTMENT_SENIORITY}
					name="position"
					value={IcpPosition.DEPARTMENT_SENIORITY}
					onChange={() =>
						setTitleFilters({
							...titleFilters,
							[prio]: {
								...titleFilters?.[prio],
								position: IcpPosition.DEPARTMENT_SENIORITY,
								title: undefined,
								similarTitles: [],
								similarTitleSearch: false,
								personaCategoryId: undefined,
							},
						})
					}
				>
					<span className="text-text-meta ml-2 flex items-center gap-1.5">
						{ts("departmentAndSeniority")}
						<SearchEngineBadge meta={SEARCH_ENGINE_BY_POSITION[IcpPosition.DEPARTMENT_SENIORITY]} />
					</span>
				</Radio>
				{titleFilters?.[prio]?.position === IcpPosition.DEPARTMENT_SENIORITY && (
					<div className="mt-2 ml-8 flex flex-col items-start self-stretch">
						<div className="flex flex-col gap-4 w-full mb-4">
							<MultiAutoComplete
								data={SENIORITY_OPTIONS}
								handleSelect={(value) => {
									setTitleFilters({
										...titleFilters,
										[prio]: {
											...titleFilters?.[prio],
											seniority: value?.map((r) => r.id.toString()) || [],
										},
									});
								}}
								label={ts("seniorityLabel")}
								placeholder={ts("seniorityPlaceholder")}
								selectedItems={
									titleFilters?.[prio]?.seniority?.map((r) => ({
										id: r,
										title: SENIORITY_OPTIONS.find((s) => s.id === r)?.title || r,
									})) || []
								}
							/>
						</div>
						<div className="flex flex-col gap-4 w-full mb-4">
							<MultiAutoComplete
								data={DEPARTMENT_OPTIONS}
								handleSelect={(value) => {
									setTitleFilters({
										...titleFilters,
										[prio]: {
											...titleFilters?.[prio],
											department: value?.map((r) => r.id.toString()) || [],
										},
									});
								}}
								label={ts("departmentLabel")}
								placeholder={ts("departmentPlaceholder")}
								selectedItems={
									titleFilters?.[prio]?.department?.map((r) => ({
										id: r,
										title: DEPARTMENT_OPTIONS.find((d) => d.id === r)?.title || r,
									})) || []
								}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
